import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { ui_softwareReleasesVersionProgressQuery } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionProgressQuery.graphql';
import type { ui_versionProgress_VersionProgress$key } from '@atlassian/jira-relay/src/__generated__/ui_versionProgress_VersionProgress.graphql';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { CollapsibleSection } from '@atlassian/jira-software-releases-collapsible-ui-toggle/src/ui/index.tsx';
import { IssuesProgressBar } from './issue-progress-bar/index.tsx';
import { IssueProgressDetails } from './issue-progress-details/index.tsx';
import { IssuesHeading } from './issues-heading/index.tsx';
import { messages } from './messages.tsx';
import { RelatedWorkHeading } from './related-work-heading/index.tsx';
import { RelatedWorkProgressBar } from './related-work-progress-bar/index.tsx';

type Props = {
	queryRef: PreloadedQuery<ui_softwareReleasesVersionProgressQuery>;
};

export const VersionProgress = ({ queryRef }: Props) => {
	const { jira } = usePreloadedQuery<ui_softwareReleasesVersionProgressQuery>(
		graphql`
			query ui_softwareReleasesVersionProgressQuery(
				$versionAri: ID!
				$isDevopsFeatureDisabledInFedRamp: Boolean!
			) {
				jira @required(action: THROW) {
					version(id: $versionAri) @required(action: THROW) {
						...ui_versionProgress_VersionProgress
					}
				}
			}
		`,
		queryRef,
	);
	const versionFrag: ui_versionProgress_VersionProgress$key = jira.version;

	const version = useFragment(
		graphql`
			fragment ui_versionProgress_VersionProgress on JiraVersion {
				relatedWorkV2(first: 100)
					@connection(key: "version_relatedWorkV2")
					@required(action: THROW)
					@skip(if: $isDevopsFeatureDisabledInFedRamp) {
					edges @required(action: THROW) {
						...relatedWorkHeading_versionProgress
						...relatedWorkProgressBar_versionProgress
					}
				}
				...issuesHeading_versionProgress
				...issueProgressBar_versionProgress_IssuesProgressBar
				...issueProgressDetails_versionProgress
				...ui_softwareReleasesCollapsibleUiToggle_CollapsibleSection
			}
		`,
		versionFrag,
	);

	const { formatMessage } = useIntl();
	const edges = version.relatedWorkV2 ? version.relatedWorkV2.edges.filter(Boolean) : [];

	return (
		<Container>
			<MainHeading>
				<Heading size="xsmall" as="h3">
					{formatMessage(messages.progressHeading)}
				</Heading>
			</MainHeading>
			{!isDevopsFeatureDisabledInFedRamp() && (
				<div>
					<RelatedWorkHeading relatedWorkEdges={edges} />
					<RelatedWorkProgressBar relatedWorkEdges={edges} />
				</div>
			)}
			<div>
				<CollapsibleSection
					jiraVersion={version}
					uiType="PROGRESS_CARD"
					renderHeader={(isCollapsed, onToggleClicked) => (
						<>
							<IssuesHeading
								jiraVersion={version}
								isCollapsed={isCollapsed}
								onToggleClicked={onToggleClicked}
							/>
							<IssuesProgressBar jiraVersion={version} />
						</>
					)}
					renderBody={() => <IssueProgressDetails jiraVersion={version} />}
				/>
			</div>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	paddingTop: token('space.200', '16px'),
	paddingRight: token('space.300', '24px'),
	paddingBottom: token('space.300', '24px'),
	paddingLeft: token('space.300', '24px'),
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MainHeading = styled.div({
	paddingTop: token('space.100', '8px'),
	paddingRight: 0,
	paddingBottom: token('space.100', '8px'),
	paddingLeft: 0,
});
